import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AddonOptions,
  EnvironmentName,
  SensibleWidget,
} from '@sensible-weather/widget-react';
import { v4 as uuidv4 } from 'uuid';
import { Tooltip } from 'react-tooltip';
import Footer from '../../components/Footer/Footer';
import Combobox, {
  ComboboxItem,
} from '../../sharedComponents/ComboBox/ComboBox';
import Button from '../../sharedComponents/Button/Button';
import { Select } from '../../sharedComponents/Select/Select';
import Typography from '../../sharedComponents/Typography/Typography';
import DateRangeInput from '../../sharedComponents/DateRangeInput/DateRangeInput';
import TextInput from '../../sharedComponents/TextInput/TextInput';
import { assets } from '../../sharedComponents/assets';
import { ConfigContext } from '../../contexts';
import { GoogleMapsAPI } from '../../api';
import { useStore } from '../../store/hooks';
import { PlaceSuggestion } from '../../api/googleMaps.types';
import { PageProps } from '../Page.types';
import {
  CurrencyUnit,
  currencyUnitToSymbol,
} from '../../utils/types/Currency/Currency.types';
import styles from './purchase-guarantee.module.scss';
import IconTextButton from '../../sharedComponents/IconTextButton/IconTextButton';
import { Refresh } from '../../sharedComponents/assets/svg-tsx/buttonIcons';

type CurrencyOption = {
  code: CurrencyUnit;
  label: string;
};

type WidgetData = {
  rendered: boolean;
  lat: number | undefined;
  long: number | undefined;
  currency: CurrencyUnit | string;
  coverageEndDate: string;
  coverageStartDate: string;
  exposureName: string;
  amount: number | undefined;
  langLocale: string;
};

type FormData = {
  address: string;
  currency: CurrencyUnit | string;
  cost: string;
  dates: (Date | null) | (Date | null)[];
};
const PurchaseGuarantee: React.FunctionComponent<PageProps> = ({
  layout: Layout,
  navigation: Navigation,
}) => {
  const {
    user: { user },
  } = useStore();

  const { t } = useTranslation();

  const defaultForm = {
    address: '',
    currency: '',
    cost: '',
    dates: null,
  };

  const defaultWidgetData = {
    rendered: false,
    lat: 0,
    long: 0,
    currency: CurrencyUnit.USD,
    coverageEndDate: '',
    coverageStartDate: '',
    exposureName: '',
    amount: 0,
    langLocale: user.langLocale ?? 'en-us',
  };
  const [form, setForm] = useState<FormData>(defaultForm);
  const [isFormValid, setIsFormValid] = useState(false);
  const [widgetData, setWidgetData] = useState<WidgetData>(defaultWidgetData);
  const [activeInput, setActiveInput] = useState<{
    currency: boolean;
    calendar: boolean;
    address: boolean;
  }>({ currency: false, calendar: false, address: false });
  const [uuid, setUuid] = useState('');

  useEffect(() => {
    const isValid = Object.values(form).every(
      (value) => value !== null && value !== undefined && value !== '',
    );
    setIsFormValid(isValid);
  }, [form]);

  const configEnvelope = useContext(ConfigContext);

  const Info = assets.svg.sensible.Info;
  useEffect(() => {
    setUuid(() => uuidv4());
  }, []);

  const currencyOptions: CurrencyOption[] = Object.values(CurrencyUnit).map(
    (code) => ({
      code,
      label: `${code.toUpperCase()} (${currencyUnitToSymbol(code)})`,
    }),
  );
  const dateToString = (date: Date) => date.toLocaleDateString('sv');
  const formatCoverageDates = (
    dates: Date[],
  ): { start: string; end: string } => {
    if (dates.length > 1) {
      return { start: dateToString(dates[0]), end: dateToString(dates[1]) };
    } else {
      return { start: dateToString(dates[0]), end: dateToString(dates[0]) };
    }
  };

  const { getAccessTokenSilently } = useAuth0();

  async function getAutoComplete(value: string): Promise<ComboboxItem[]> {
    const token = await getAccessTokenSilently();
    const suggestions: PlaceSuggestion[] =
      await GoogleMapsAPI.getAutoCompletePredictions(
        value,
        user.langLocale ?? 'en-us',
        uuid,
        token,
      );

    return suggestions.map((aco) => {
      return {
        value: aco.placePrediction.text.text,
        label: aco.placePrediction.text.text,
      };
    });
  }

  const formSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const coverageDates = formatCoverageDates(form.dates as Date[]);
    try {
      const token = await getAccessTokenSilently();
      const coords = await GoogleMapsAPI.getCoordinates(token, form.address);
      setWidgetData({
        rendered: true,
        lat: coords?.lat,
        long: coords?.lng,
        currency: form.currency,
        coverageEndDate: coverageDates.start,
        coverageStartDate: coverageDates.end,
        exposureName: form.address,
        amount: parseFloat(form.cost),
        langLocale: user.langLocale ?? 'en-us',
      });
    } catch (error) {
      console.error('Failed to get coordinates:', error);
    }
  };
  const handleFieldChange =
    (field: keyof typeof form) => (value: string | number) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    };

  //TODO #2755: replace all hardcoded strings with Hygraph keys
  return (
    <Layout navigation={<Navigation />} footer={<Footer />}>
      <div className={styles.container}>
        <div className={styles.explainer}>
          <Typography variant="h4">
            {t('sunshine.content.purchase_guarantee.explainer.header')}
          </Typography>
          <Typography variant="body-1">
            {t('sunshine.content.purchase_guarantee.explainer')}
          </Typography>{' '}
          <Typography variant="body-1">
            {t('sunshine.content.purchase_guarantee.explainer_list_header')}
          </Typography>{' '}
          <Typography variant="body-1">
            <ul>
              <li>
                {t('sunshine.content.purchase_guarantee.explainer_list_1')}
              </li>
              <li>
                {t('sunshine.content.purchase_guarantee.explainer_list_2')}
              </li>
              <li>
                {t('sunshine.content.purchase_guarantee.explainer_list_3')}
              </li>
            </ul>
          </Typography>
          <Typography variant="body-1">
            {t('sunshine.content.purchase_guarantee.explainer_signoff')}
          </Typography>
        </div>
        {!widgetData.rendered ? (
          <form onSubmit={formSubmit} className={styles.form}>
            <Typography variant="h4" align="center">
              {t('sunshine.upcoming_reservation.header')}
            </Typography>
            <Combobox
              value={form.address ?? ''}
              onChange={handleFieldChange('address')}
              onFocus={() => {
                setActiveInput((prev) => ({
                  ...prev,
                  address: true,
                }));
              }}
              getAsyncItems={async (query: string) =>
                await getAutoComplete(query)
              }
              label={t('sunshine.label.reservation_address')}
              placeholder={t('sunshine.label.reservation_address')}
              noResultsMessage={t('generic.label.no_results')}
              data-testid="trip-details-trip-location-combobox"
              tooltipIcon={
                <span data-tooltip-id="address-info-tooltip">
                  <Info height={'14'} />
                  <Tooltip
                    className={styles.tooltip}
                    id="address-info-tooltip"
                    place="right"
                    content={t('sunshine.content.search_address_info')}
                  />
                </span>
              }
            ></Combobox>
            <Select<CurrencyOption>
              label={t('generic.label.currency')}
              showLabel={activeInput.currency}
              placeholder={t('generic.label.currency')}
              value={
                currencyOptions.find((opt) => opt.code === form.currency) ??
                undefined
              }
              onChange={(selected) => {
                setForm((prev) => ({
                  ...prev,
                  currency: selected?.code ?? '',
                }));
              }}
              items={currencyOptions}
              itemToString={(item) => item?.label ?? ''}
              onFocus={() => {
                setActiveInput((prev) => ({
                  ...prev,
                  currency: true,
                }));
              }}
              onBlur={() => {
                setActiveInput((prev) => ({
                  ...prev,
                  currency: form.currency !== '',
                }));
              }}
            />
            <TextInput
              labelFormat={'none'}
              value={form.cost}
              handleChange={handleFieldChange('cost')}
              label={t('sunshine.label.total_reservation_cost')}
            />
            <DateRangeInput
              lang={user.langLocale ?? 'en-us'}
              label={t('sunshine.label.reservation_dates')}
              showLabel={activeInput.calendar}
              onChange={(value: (Date | null) | (Date | null)[]) =>
                setForm((prev) => ({
                  ...prev,
                  dates: value,
                }))
              }
              onFocus={() => {
                setActiveInput((prev) => ({
                  ...prev,
                  calendar: true,
                }));
              }}
              name={'date-range'}
              data-testid={'data-testid'}
            ></DateRangeInput>
            <div className={styles.btn}>
              <Button
                clickHandling="submit"
                label={t('sunshine.action.calculate_guarantee')}
                disabled={!isFormValid}
                errorMsg=""
              />
            </div>
          </form>
        ) : (
          <div className={styles.widget}>
            <SensibleWidget
              partnerID={configEnvelope.config?.partnerID}
              productID={configEnvelope.config?.productID}
              __environment={
                window.location.host.includes('dev') ||
                window.location.host.includes('localhost')
                  ? EnvironmentName.DEV
                  : EnvironmentName.PRODUCTION
              }
              quoteParameters={{
                coverageEndDate: widgetData.coverageStartDate,
                coverageStartDate: widgetData.coverageEndDate,
                currency: widgetData.currency,
                exposureLatitude: widgetData.lat,
                exposureLongitude: widgetData.long,
                exposureName: form.address,
                exposureTotalCoverageAmount: widgetData.amount,
                langLocale: user.langLocale,
              }}
              addon={AddonOptions.CHECKOUT}
            />
            <IconTextButton
              icon={Refresh}
              label={t('sunshine.action.calculate_another_guarantee')}
              variant={'secondary'}
              onClickHandler={() => {
                setWidgetData(() => ({ ...defaultWidgetData }));
                setForm(() => ({ ...defaultForm }));
                setActiveInput(() => ({
                  currency: false,
                  calendar: false,
                  address: false,
                }));
              }}
              disabled={false}
            ></IconTextButton>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PurchaseGuarantee;
